<script setup lang="ts">
import { mdiOpenInNew, mdiLockOutline } from '@mdi/js'
import type { ExternalLinks } from '~/composables/api/useUserApi'

/**
 * 魔導士の名のり 編集ダイアログ (#417)
 *
 * 初回設定 (slug 未設定) と編集 (slug あり) を同じ UI で扱う。slug は
 * 一度設定されると 30 日のクールダウン中は変更不可。bio/links は
 * ダイアログを開いたタイミングで getPublicCreator から fetch する。
 *
 * 呼び出し元は単に `v-model="dialog"` で開けばよく、bio/links を
 * pre-fetch して props で渡す必要はない。
 */

const dialog = defineModel<boolean>()
const emit = defineEmits<{
  saved: []
}>()

const { updateUserAttribute, getPublicCreator } = useApi()
const gameStateStore = useGameStateStore()
const { user: userInfo } = storeToRefs(gameStateStore)
const { reloadGameState } = gameStateStore
const { triggerEventAndProcess } = useEventSystem()

const SLUG_PATTERN = /^[a-z0-9][a-z0-9\-_]{1,29}$/
const SLUG_MAX = 30
const COOLDOWN_DAYS = 30
const BIO_MAX = 1000

const editForm = reactive<{
  slug: string
  bioMarkdown: string
  externalLinks: { x: string, github: string, itch: string, pixiv: string }
}>({
  slug: '',
  bioMarkdown: '',
  externalLinks: { x: '', github: '', itch: '', pixiv: '' },
})
const editError = ref('')
const isSaving = ref(false)
const isLoading = ref(false)

// 初回設定 (slug が未設定) かどうか — submit 成功後のイベント発火条件に使う
const isFirstTime = computed(() => !userInfo.value?.slug)

// 公開ページプレビュー (slug が設定済のときだけ表示)
const previewHref = computed(() =>
  userInfo.value?.slug ? `/c/${userInfo.value.slug}` : null,
)

// slug クールダウン判定。設定済 + slugChangedAt が 30 日以内なら true。
const slugLocked = computed(() => {
  const changedAt = userInfo.value?.slugChangedAt
  if (!userInfo.value?.slug || !changedAt) return false
  const changed = new Date(changedAt).getTime()
  if (Number.isNaN(changed)) return false
  return Date.now() < changed + COOLDOWN_DAYS * 24 * 60 * 60 * 1000
})

// ダイアログが開いたら、現在の bio/links を fetch してフォームを seed する。
watch(dialog, async (open) => {
  if (!open) return
  editError.value = ''
  editForm.slug = userInfo.value?.slug ?? ''
  editForm.bioMarkdown = ''
  editForm.externalLinks = { x: '', github: '', itch: '', pixiv: '' }

  if (!userInfo.value?.slug) {
    // 初回設定 — 空フォームのまま
    return
  }
  isLoading.value = true
  try {
    const creator = await getPublicCreator(userInfo.value.slug)
    editForm.bioMarkdown = creator.bioMarkdown ?? ''
    editForm.externalLinks = {
      x: creator.externalLinks?.x ?? '',
      github: creator.externalLinks?.github ?? '',
      itch: creator.externalLinks?.itch ?? '',
      pixiv: creator.externalLinks?.pixiv ?? '',
    }
  }
  catch { /* fetch 失敗時は空フォームで継続 */ }
  finally {
    isLoading.value = false
  }
})

const submitEdit = async () => {
  isSaving.value = true
  editError.value = ''
  const wasFirstTime = isFirstTime.value

  // slug の事前バリデーション (フロント側でも軽くチェック)
  if (!editForm.slug.trim()) {
    editError.value = '名のりを入力してください。'
    isSaving.value = false
    return
  }
  if (!SLUG_PATTERN.test(editForm.slug.trim().toLowerCase())) {
    editError.value = '半角英数 / ハイフン / アンダースコアのみ。先頭は英数字。2 〜 50 文字。'
    isSaving.value = false
    return
  }

  try {
    const links: ExternalLinks = {
      x: editForm.externalLinks.x?.trim() || null,
      github: editForm.externalLinks.github?.trim() || null,
      itch: editForm.externalLinks.itch?.trim() || null,
      pixiv: editForm.externalLinks.pixiv?.trim() || null,
    }
    // クールダウン中は slug は送信しない (サーバー側でも弾かれるが念のため)。
    const payload: Parameters<typeof updateUserAttribute>[0] = {
      bioMarkdown: editForm.bioMarkdown.trim() || null,
      externalLinks: links,
    }
    if (!slugLocked.value) {
      payload.slug = editForm.slug.trim().toLowerCase()
    }
    await updateUserAttribute(payload)
    // slug を反映するため game state を再ロード。
    await reloadGameState()

    // 初回 (この保存以前は slug 未設定) のときだけ、センパイの
    // シナリオイベントを発火する。
    if (wasFirstTime) {
      try {
        await triggerEventAndProcess('CLICK:firstNanori')
      }
      catch (e) {
        console.error('[firstNanori] event trigger failed', e)
      }
    }

    dialog.value = false
    emit('saved')
  }
  catch (e: unknown) {
    const err = e as { data?: { message?: string }, message?: string }
    editError.value = err?.data?.message ?? err?.message ?? '保存に失敗しました'
  }
  finally {
    isSaving.value = false
  }
}

const linkSlots = [
  { key: 'x' as const, label: 'X (Twitter)', placeholder: 'https://x.com/your-handle' },
  { key: 'github' as const, label: 'GitHub', placeholder: 'https://github.com/your-handle' },
  { key: 'itch' as const, label: 'itch.io', placeholder: 'https://your-handle.itch.io' },
  { key: 'pixiv' as const, label: 'Pixiv', placeholder: 'https://www.pixiv.net/users/12345' },
]
</script>

<template>
  <DialogLargeDialog
    v-model="dialog"
    :title="isFirstTime ? '魔導士の名のりを作成' : '魔導士の名のりを編集'"
    :submit-button="{ title: '保存' }"
    :cancel-button="{ title: 'キャンセル' }"
    @submit="submitEdit"
    @cancel="dialog = false"
  >
    <template #hint>
      <div class="hint-actions">
        <a
          v-if="previewHref"
          :href="previewHref"
          target="_blank"
          rel="noopener"
          class="preview-link"
        >
          公開ページをプレビュー
          <v-icon
            :icon="mdiOpenInNew"
            size="x-small"
          />
        </a>
      </div>
    </template>

    <div class="body-scroll">
      <v-alert
        v-if="editError"
        type="error"
        variant="tonal"
        density="compact"
        class="mb-3"
      >
        {{ editError }}
      </v-alert>

      <h3 class="section-label">
        名のり (handle)
      </h3>
      <div class="slug-row">
        <v-text-field
          v-model="editForm.slug"
          :disabled="slugLocked || isSaving"
          :prepend-inner-icon="slugLocked ? mdiLockOutline : undefined"
          :maxlength="SLUG_MAX"
          placeholder="your-handle"
          variant="outlined"
          density="compact"
          hide-details
          class="edit-input slug-input"
        />
        <span
          v-if="slugLocked"
          class="slug-lock-note"
        >
          名のりは 30 日間は編集できません
        </span>
      </div>
      <p
        v-if="!slugLocked"
        class="form-note"
      >
        半角英数 / ハイフン / アンダースコアのみ。先頭は英数字。
      </p>

      <h3 class="section-label section-label--with-gap">
        自己紹介
      </h3>
      <v-textarea
        v-model="editForm.bioMarkdown"
        rows="6"
        :counter="BIO_MAX"
        :maxlength="BIO_MAX"
        variant="outlined"
        density="compact"
        persistent-counter
        :loading="isLoading || isSaving"
        class="edit-input"
        no-resize
        placeholder="あなたのことや、これまでに作った Pyxel 作品のことなど。"
      />
      <p class="form-note">
        Markdown が使えます（見出し / 強調 / リスト / リンク / 画像 / 引用 / コードブロック）。<br>
        UTM 等のトラッキングパラメータは保存時に自動で取り除かれます。短縮 URL は使えません。
      </p>
      <div
        v-if="editForm.bioMarkdown.trim()"
        class="preview"
      >
        <div class="preview-label">
          プレビュー（公開ページでの見え方）
        </div>
        <!-- 閲覧ページと同じ CreatorBio コンポーネントを使い、同じ背景の
             ミニウィンドウとして見せる。編集 UI が fantasy 体裁なのと対比的に
             ニュートラルなページの雰囲気を残す。 -->
        <div class="preview-frame">
          <CreatorBio :markdown="editForm.bioMarkdown" />
        </div>
      </div>

      <h3 class="section-label section-label--with-gap">
        外部リンク
      </h3>
      <v-text-field
        v-for="slot in linkSlots"
        :key="slot.key"
        v-model="editForm.externalLinks[slot.key]"
        :label="slot.label"
        :placeholder="slot.placeholder"
        variant="outlined"
        density="compact"
        class="mb-2 edit-input"
      />
    </div>
  </DialogLargeDialog>
</template>

<style scoped>
.body-scroll {
  height: 100%;
  overflow-y: auto;
  padding-right: max(4px, calc(0.5 * var(--u)));
  scrollbar-color: rgba(92, 44, 12, 0.5) transparent;
  scrollbar-width: thin;
}

.section-label {
  font-family: "Utsukushi", serif;
  font-weight: bold;
  text-align: left;
  font-size: max(14px, calc(2.0 * var(--u)));
  color: #5c2c0c;
  letter-spacing: 0.05em;
  margin: 0.4em 0 0.4em;
  padding-bottom: 0.15em;
  border-bottom: 1px solid rgba(92, 44, 12, 0.3);
}

.section-label--with-gap {
  margin-top: max(16px, calc(2 * var(--u)));
}

/* slug 入力欄と、クールダウン中のロック注記を横並びで配置する。 */
.slug-row {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
}

/* 30 文字 + ロックアイコン分の余裕を見て約 28em。プロポーショナル
   フォントなので 30 文字きっちりではなく少し余裕がある幅。 */
.slug-input {
  width: 28em;
  max-width: 100%;
  flex: 0 1 auto;
}

.slug-lock-note {
  font-family: "Noto Sans JP", sans-serif;
  font-size: max(11px, calc(1.3 * var(--u)));
  color: rgba(92, 44, 12, 0.6);
  font-style: italic;
}

.form-note {
  font-family: "Noto Sans JP", sans-serif;
  font-size: max(10px, calc(1.15 * var(--u)));
  color: rgba(92, 44, 12, 0.7);
  line-height: 1.35;
  margin: 0.3em 0 0.8em;
}

.edit-input :deep(.v-field) {
  background: rgba(255, 248, 230, 0.55);
  font-family: "Noto Sans JP", sans-serif;
}

.edit-input :deep(.v-field--disabled) {
  background: rgba(180, 150, 110, 0.18);
}

.edit-input :deep(.v-label) {
  color: #5c2c0c !important;
}

.preview {
  margin: 0.6rem 0 1rem;
}

.preview-label {
  font-family: "Utsukushi", serif;
  text-align: left;
  font-size: max(11px, calc(1.3 * var(--u)));
  color: rgba(92, 44, 12, 0.7);
  letter-spacing: 0.05em;
  margin-bottom: 0.4rem;
}

/* 閲覧ページ /c/<slug> のカード本体と同じ背景・縁取りでプレビューを
   囲むことで、「実際に世界に出るときの見え方」を窓越しに見せる感を出す。
   bio 本体の描画は CreatorBio に共通化済み。 */
.preview-frame {
  background: linear-gradient(135deg, #f5e8d6 0%, #ecdbc1 100%);
  border: 1px solid rgba(140, 100, 50, 0.35);
  border-radius: 4px;
  padding: 0.9rem 1.1rem;
  box-shadow: inset 0 1px 3px rgba(0, 0, 0, 0.06);
}

.hint-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
}

.preview-link {
  display: inline-flex;
  align-items: center;
  gap: 0.3em;
  font-family: "Noto Sans JP", sans-serif;
  font-size: max(11px, calc(1.3 * var(--u)));
  color: #5c2c0c;
  text-decoration: underline;
  text-decoration-color: rgba(92, 44, 12, 0.4);
  white-space: nowrap;
}

.preview-link:hover {
  text-decoration-color: #5c2c0c;
}
</style>
